/**
 * Utility functions for the Date Filter Picker in Analytics Hub.
 * Derives available dates from the existing period-weeks API response.
 */

import type { YearData } from '../../../api/periodWeeksApi';
import type { DateSelectionState } from '../types/dateSelectionTypes';

// ─── API Date Range Derivation ────────────────────────────────────────────────

/**
 * Derive startDate/endDate for API payloads from DateSelectionState.
 * Guarantees: either both are present, or neither — the API never receives
 * only one of startDate/endDate.
 *
 * Dates are already stored as YYYY-MM-DD strings — no formatting needed.
 * Reusable across KPI Overview, One Pager, Reports, Export APIs.
 */
export function getApiDateRange(
	selection: DateSelectionState
): { startDate?: string; endDate?: string } {
	if (selection.mode === 'SINGLE_DATE' && selection.selectedDate) {
		return { startDate: selection.selectedDate, endDate: selection.selectedDate };
	}
	if (selection.mode === 'DATE_RANGE' && selection.startDate && selection.endDate) {
		return { startDate: selection.startDate, endDate: selection.endDate };
	}
	return {};
}

/**
 * Extract all available ISO date strings for the given selected weekInYear numbers
 * from the already-fetched yearData (Redux state).
 *
 * @param yearData - The full period-weeks API response stored in Redux
 * @param selectedWeekInYears - Array of selected weekInYear numbers (e.g. [14, 15])
 * @param selectedPeriods - Optional array of selected period numbers to narrow scope
 * @returns Array of ISO date strings (e.g. ["2026-03-29", "2026-03-30", ...])
 */
export function getAvailableDatesForWeek(
	yearData: YearData[],
	selectedWeekInYears: number[],
	selectedPeriods: number[] = []
): string[] {
	if (!yearData || yearData.length === 0 || selectedWeekInYears.length === 0) {
		return [];
	}

	const dates: string[] = [];

	for (const yd of yearData) {
		for (const pd of yd.periods) {
			// If specific periods are selected, only look at those periods
			if (selectedPeriods.length > 0 && !selectedPeriods.includes(pd.period)) {
				continue;
			}
			for (const weekObj of pd.weeks) {
				if (selectedWeekInYears.includes(weekObj.weekInYear)) {
					if (weekObj.days && weekObj.days.length > 0) {
						for (const day of weekObj.days) {
							if (day.date && !dates.includes(day.date)) {
								dates.push(day.date);
							}
						}
					}
				}
			}
		}
	}

	// Sort chronologically
	dates.sort();
	return dates;
}

/**
 * Extract available ISO date strings dynamically based on the current filter state.
 * Handles all three scenarios:
 * - Week(s) selected → dates from those specific weeks only
 * - Period(s) selected, no week → all dates from all weeks within those periods
 * - Neither period nor week selected (All) → all dates across the entire fiscal year
 *
 * The Date Picker should always be enabled as long as yearData is available.
 *
 * @param yearData - The full period-weeks API response stored in Redux
 * @param selectedWeekInYears - Array of selected weekInYear numbers (empty = "All")
 * @param selectedPeriods - Array of selected period numbers (empty = "All")
 * @returns Sorted array of ISO date strings
 */
export function getAvailableDates(
	yearData: YearData[],
	selectedWeekInYears: number[],
	selectedPeriods: number[]
): string[] {
	if (!yearData || yearData.length === 0) {
		return [];
	}

	const dates: string[] = [];

	for (const yd of yearData) {
		for (const pd of yd.periods) {
			// If specific periods are selected, only include those periods
			if (selectedPeriods.length > 0 && !selectedPeriods.includes(pd.period)) {
				continue;
			}
			for (const weekObj of pd.weeks) {
				// If specific weeks are selected, only include those weeks
				if (selectedWeekInYears.length > 0 && !selectedWeekInYears.includes(weekObj.weekInYear)) {
					continue;
				}
				if (weekObj.days && weekObj.days.length > 0) {
					for (const day of weekObj.days) {
						if (day.date && !dates.includes(day.date)) {
							dates.push(day.date);
						}
					}
				}
			}
		}
	}

	// Sort chronologically
	dates.sort();
	return dates;
}

/**
 * Check whether a given ISO date string is within the list of available dates.
 *
 * @param date - ISO date string to validate (e.g. "2026-03-31")
 * @param availableDates - Array of allowed ISO date strings
 * @returns true if the date is in the allowed list
 */
export function isValidDateSelection(date: string, availableDates: string[]): boolean {
	return availableDates.includes(date);
}

/**
 * Format an ISO date string to display format "MMM DD, YYYY".
 *
 * @param isoDate - ISO date string (e.g. "2026-03-31")
 * @returns Formatted string (e.g. "Mar 31, 2026")
 */
export function formatDateDisplay(isoDate: string): string {
	const months = [
		'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
		'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
	];

	// Parse as local date (avoid timezone offset issues)
	const parts = isoDate.split('-');
	const year = parts[0];
	const monthIndex = parseInt(parts[1], 10) - 1;
	const day = parseInt(parts[2], 10);

	const monthStr = months[monthIndex] || '';
	const dayStr = day < 10 ? `0${day}` : `${day}`;

	return `${monthStr} ${dayStr}, ${year}`;
}
